import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, SelectProps, TextField, Checkbox, Radio, RadioGroup, FormControlLabel, Box, Chip } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CancelIcon from "@mui/icons-material/Cancel";
import { FieldAttributes, Form, Formik, useField } from 'formik';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { StyledDropzone } from '../Elements/Dropzone';
import * as yup from 'yup';

export interface AddReportTagModel {
    partnersId: number;
    stateId: number;
    isMultipleTags?: boolean;
    numberOfTags: string;
    invoiceNumber: string;
    insuredName: string;
    policyNumber: string;
    tagNumber: string;
    errorTagCodes: number[];
    files: File[];
    filerNotes: string;
};

interface Iprops {
    handleClose: () => void;
    Open: boolean;
    partners: { partnersId: number, customerName: string }[];
    states: { stateId: number, stateName: string }[];
    errorTagCodes: { errorTagCodeId: number, errorTagName: string }[];
}

const AddReportTagValidationSchema = yup.object({
    partnersId: yup.number()
        .required('Client is required')
        .min(1, 'Please select a Client'),
    stateId: yup.number()
        .required('State is required')
        .min(1, 'Please select a State'),
    numberOfTags: yup.string()
        .required('Number of Tags is required')
        .oneOf(['single', 'multiple'], 'Please select Single or Multiple'),
    invoiceNumber: yup.string().when('numberOfTags', {
        is: 'single',
        then: (schema) => schema.required('Invoice Number is required for single tags'),
        otherwise: (schema) => schema
    }),
    insuredName: yup.string().when('numberOfTags', {
        is: 'single',
        then: (schema) => schema.required('Insured Name is required for single tags'),
        otherwise: (schema) => schema
    }),
    policyNumber: yup.string().when('numberOfTags', {
        is: 'single',
        then: (schema) => schema.required('Policy Number is required for single tags'),
        otherwise: (schema) => schema
    }),
    tagNumber: yup.string()
        .optional()
        .matches(/^\d*$/, 'Tag Number must be numeric'),
    errorTagCodes: yup.array()
        .of(yup.number())
        .min(1, 'At least one Error Type Code must be selected')
        .required('At least one Error Type Code must be selected'),
    // file: yup.mixed<File>()
    //     .required('A file must be selected')
    //     .test('file-required', 'A file must be selected', (value) => value !== null && value !== undefined),
    filerNotes: yup.string()
});

export const AddReportTag: React.FC<Iprops> = ({ handleClose, Open, partners, states, errorTagCodes }) => {
    const { user } = useAuth();
    const token = user?.token;
    const GreenBtnColor = '#4caf50';
    const OrangeBtnColor = '#ff7b00';
    const PrimaryBlueColor = '#0d4a63';
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        partnersId: 0,
        stateId: 0,
        numberOfTags: '',  // single or multiple
        invoiceNumber: '',
        insuredName: '',
        policyNumber: '',
        tagNumber: '',
        errorTagCodes: [],
        files: [] as File[],
        filerNotes: '',
    });
    
    const FMSelectPartnersCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>Client</FormHelperText>
                <Select {...field}
                    labelId="Partner-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}>
                    {partners.map((partner, key) =>
                        <MenuItem key={key} value={partner.partnersId}>{partner.customerName}</MenuItem>
                    )}
                </Select>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    const FMSelectStates: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta] = useField<{}>(props);
        return (
            <FormControl fullWidth error={meta.touched && !!meta.error}>
                <FormHelperText className='formtexthelper-heading'>State</FormHelperText>
                <Select {...field}
                    labelId="State-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}>
                    {states.map((state, key) => (
                        <MenuItem key={key} value={state.stateId}>{state.stateName}</MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && (
                    <FormHelperText error>{meta.error}</FormHelperText>
                )}
            </FormControl>
        );
    };
    return (
        <Dialog open={Open} onClose={handleClose} maxWidth='xl' sx={{ width: '80%', margin: '0 auto' }}>
            {loading && <LinearProgress style={{ margin: 2 }} />}
            <h2 style={{ marginLeft: "1vw" }} className='card-title'>Create New Tag</h2>
            <Formik 
                initialValues={formData}
                enableReinitialize={true}
                validateOnChange={true}
                validationSchema={AddReportTagValidationSchema}
                onSubmit={(data, { setSubmitting, setErrors }) => {
                    const clientName = partners.find((partner) => partner.partnersId === data.partnersId)?.customerName || 'the selected partner';
                    Swal.fire({
                        title: `<div style="color: white;">Are you sure?</div>`,
                        html: `<div style="color: white;">This will send a tag to the Client Portal for <span style="color: yellow;">${clientName}</span></div>`,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, send it!',
                        cancelButtonText: 'No, Continue to EDIT',
                        confirmButtonColor: GreenBtnColor,
                        cancelButtonColor: OrangeBtnColor,
                        reverseButtons: true,
                        background: PrimaryBlueColor,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setSubmitting(true);
                            setLoading(true);
                            
                            // Prepare FormData for API call with files and form data
                            const formData = new FormData();
                            
                            // Append JSON data as a string field (will be in request.body)
                            formData.append("data", JSON.stringify({  // everything except files
                                partnersId: data.partnersId,
                                stateId: data.stateId,
                                numberOfTags: data.numberOfTags,
                                invoiceNumber: data.numberOfTags === 'single' ? data.invoiceNumber : '',
                                insuredName: data.numberOfTags === 'single' ? data.insuredName : '',
                                policyNumber: data.numberOfTags === 'single' ? data.policyNumber : '',
                                tagNumber: data.tagNumber || '',
                                errorTagCodes: data.errorTagCodes,
                                filerNotes: data.filerNotes,
                            }));
                            
                            // Append files - all files with the same field name for multer
                            // Multer will put them in request.files as an array
                            if (data.files && data.files.length > 0) {
                                data.files.forEach((file) => {
                                    formData.append("files", file);
                                });
                            }
                            
                            apiClient('/ReportTags/addReportTag', {
                                method: 'POST',
                                headers: new Headers({
                                    // Don't set Content-Type header - browser will set it with boundary for FormData
                                    'Authorization': `Bearer ${token}`,
                                }),
                                body: formData,
                            }).then((response) => {
                                if (!response) return;
                                if (response.status) {
                                    Swal.fire({
                                        title: 'Tag sent to client successfully',
                                        text: 'The tag has been sent to the client.',
                                        icon: 'success',
                                    });
                                    handleClose();
                                } else {
                                    throw new Error(response.message || 'Failed to send tag');
                                }
                            }).catch((error) => {
                                console.error('Error sending tag to client:', error);
                                Swal.fire({
                                    title: 'Error',
                                    text: error.message || 'Failed to send tag to client.',
                                    icon: 'error',
                                });
                            }).finally(() => {
                                setSubmitting(false);
                                setLoading(false);
                            });
                        }else{
                            setSubmitting(false);
                            setLoading(false);
                        }
                    });
                }}
            >
                {({ values, isSubmitting, setFieldValue, setFieldTouched, errors, touched, isValid }) => {
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { name, value } = e.target;
                        setFieldValue(name, value);
                        setFieldTouched(name, true);
                    };
                    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;
                        setFieldValue("numberOfTags", value);
                        // Don't mark as touched immediately - only on blur
                    };
                    return (
                        <Form className='main-form'>
                            <DialogContent sx={{ width: '100%' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FMSelectPartnersCustom name="partnersId" />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FMSelectStates name="stateId" />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl sx={{ width: '100%' }} error={touched.numberOfTags && !!errors.numberOfTags}>
                                            <FormHelperText className='formtexthelper-heading'>
                                                Number of Tags
                                            </FormHelperText>
                                            {/* select number of tags, single or multiple */}
                                            <RadioGroup row name="numberOfTags"
                                                value={values.numberOfTags}
                                                onChange={handleRadioChange}
                                                onBlur={() => setFieldTouched("numberOfTags", true)}
                                            >
                                                <FormControlLabel value="single" control={<Radio />} label="Single" />
                                                <FormControlLabel value="multiple" control={<Radio />} label="Multiple" />
                                            </RadioGroup>
                                            {touched.numberOfTags && errors.numberOfTags && (
                                                <FormHelperText error>{errors.numberOfTags}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="invoiceNumber" label="Invoice Number" 
                                                        value={values.numberOfTags === 'multiple' ? 'multiple' : values.invoiceNumber}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("invoiceNumber", true)}
                                                        error={touched.invoiceNumber && !!errors.invoiceNumber}
                                                        helperText={touched.invoiceNumber && errors.invoiceNumber}
                                                        fullWidth 
                                                        disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="insuredName" label="Insured Name" 
                                                        value={values.numberOfTags === 'multiple' ? 'multiple' : values.insuredName}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("insuredName", true)}
                                                        error={touched.insuredName && !!errors.insuredName}
                                                        helperText={touched.insuredName && errors.insuredName}
                                                        fullWidth 
                                                        disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="policyNumber" label="Policy Number" 
                                                        value={values.numberOfTags === 'multiple' ? 'multiple' : values.policyNumber}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("policyNumber", true)}
                                                        error={touched.policyNumber && !!errors.policyNumber}
                                                        helperText={touched.policyNumber && errors.policyNumber}
                                                        fullWidth 
                                                        disabled={values.numberOfTags === 'multiple'}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField name="tagNumber" label="Tag Number" 
                                                        value={values.tagNumber}
                                                        onChange={handleChange}
                                                        onBlur={() => setFieldTouched("tagNumber", true)}
                                                        error={values.tagNumber?.length >= 100}
                                                        helperText={values.tagNumber?.length >= 100 ? 'Limited to 100 characters' : ''}
                                                        fullWidth 
                                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 100 }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl sx={{ width: '100%' }} error={touched.errorTagCodes && !!errors.errorTagCodes}>
                                            <FormHelperText className='formtexthelper-heading'>
                                                Error Type Code:
                                            </FormHelperText>
                                            {errorTagCodes.map((errorTagCode: { errorTagCodeId: number, errorTagName: string }, key: number) => 
                                                <FormControlLabel key={key}
                                                    control={
                                                        <Checkbox name="errorTagCodeId" 
                                                            checked={(values.errorTagCodes as number[]).includes(errorTagCode.errorTagCodeId)}
                                                            value={errorTagCode.errorTagCodeId}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setFieldValue("errorTagCodes", [...values.errorTagCodes, Number(e.target.value)]);
                                                                } else {
                                                                    setFieldValue("errorTagCodes", (values.errorTagCodes as number[]).filter((id: number) => 
                                                                        id !== Number(e.target.value)
                                                                    ));
                                                                }
                                                                setFieldTouched("errorTagCodes", true);
                                                            }}
                                                        />
                                                    }
                                                    label={errorTagCode.errorTagName}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        marginLeft: 0,
                                                        marginRight: 0,
                                                        '& .MuiFormControlLabel-label': {
                                                            cursor: 'pointer'
                                                        }
                                                    }}
                                                />
                                            )}
                                            {touched.errorTagCodes && errors.errorTagCodes && (
                                                <FormHelperText error>{errors.errorTagCodes}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl sx={{ width: '100%' }} 
                                            // error={touched.file && !!errors.file}
                                        >
                                            <FormHelperText className='formtexthelper-heading'>
                                                Select File(s) or Drag and Drop
                                            </FormHelperText>
                                            <StyledDropzone label="Select File(s) or Drag and Drop" 
                                                onFileDrop={(files: File[]) => {
                                                    const existingFiles = values.files || [];
                                                    const updatedFiles = [...existingFiles, ...files];
                                                    setFieldValue("files", updatedFiles);
                                                    // setFieldTouched("files", true);
                                                }} 
                                            />
                                            {/* Display selected files */}
                                            {values.files && values.files.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <FormHelperText sx={{ mb: 1 }}>
                                                        Selected Files ({values.files.length}):
                                                    </FormHelperText>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {values.files.map((file, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={`${file.name}`}
                                                                onDelete={() => {
                                                                    const updatedFiles = values.files.filter((_, i) => i !== index);
                                                                    setFieldValue("files", updatedFiles);
                                                                }}
                                                                deleteIcon={<CancelIcon />}
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ 
                                                                    maxWidth: '100%',
                                                                    '& .MuiChip-label': {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            {/* {touched.file && errors.file && (
                                                <FormHelperText error>{errors.file}</FormHelperText>
                                            )} */}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl sx={{ width: '100%' }}>
                                            <FormHelperText className='formtexthelper-heading'>
                                                Optional: Insert Filer Notes
                                            </FormHelperText>
                                            <br />
                                            <TextField name="filerNotes" label="Filer Notes" 
                                                fullWidth multiline rows={4} 
                                                onChange={(e) => setFieldValue("filerNotes", e.target.value)} 
                                                value={values.filerNotes} 
                                                onBlur={() => setFieldTouched("filerNotes", true)} 
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <button className="btn-2 btn d-flex btn-primary search-btn " onClick={handleClose} type='button'>Cancel</button>
                                <button className="btn btn-primary active-btn-blue search-btn " type="submit" disabled={isSubmitting || !isValid}>Send To Client</button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};
export default AddReportTag;